import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { AccountDetail } from "@/lib/profitability-api";
import { queryKeys } from "@/lib/query";

/** Builds the detail query string, omitting defaults. */
function buildDetailQuery(range?: string, contractType?: string) {
  const params = new URLSearchParams();
  if (range) params.set("range", range);
  if (contractType && contractType !== "all") params.set("contract_type", contractType);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useAccountDetail(accountId: string, range?: string, contractType?: string) {
  return useQuery({
    // The range is part of the key: without it, switching from 2025 to 2026
    // would serve the previous range's cached rows under the new label.
    queryKey: [...queryKeys.accountDetail(accountId), range ?? "default", contractType ?? "all"],
    queryFn: async (): Promise<AccountDetail> => {
      // buildDetailQuery, not a hand-rolled string: the contract type reaches
      // the query KEY either way, so React Query refetches and the page looks
      // responsive while the server keeps receiving the same unfiltered URL
      // and returning the same numbers.
      const response = await fetch(
        `/api/accounts/${encodeURIComponent(accountId)}${buildDetailQuery(range, contractType)}`,
      );
      if (!response.ok) {
        throw await engineError(response, "Could not load this account");
      }
      return response.json();
    },
    enabled: accountId.length > 0,
    // Month-end close data. Re-fetching on every window focus would hammer the
    // engine for numbers that change once a month.
    staleTime: 5 * 60 * 1000,
  });
}
